import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';

// Matches `duration-200`. A timer: `transitionend` never fires on a cancelled or no-op run, leaving it clipped.
const DURATION_MS = 200;

// In the markup, since the effect runs after hydration; one object, so React never overwrites the effect's height.
const CLOSED_STYLE = { height: 0, overflow: 'hidden' } as const;

/**
 * Collapses and reveals its children, keeping them mounted. Measured pixels, not `grid-template-rows:
 * 0fr -> 1fr`, which re-measures the subtree every frame; open, it returns to `auto` so focus rings show.
 */
export function Reveal({ open, children, className }: { open: boolean; children: ReactNode; className?: string }) {
	const ref = useRef<HTMLDivElement>(null);
	// Reading the prop would rewrite the height the effect owns on every re-render.
	const initialOpen = useRef(open).current;
	// Animating on mount would replay the reveal on every entry to the step.
	const isFirstPass = useRef(true);

	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;

		if (isFirstPass.current) {
			isFirstPass.current = false;
			settle(element, open);
			return;
		}

		// From where it is, so a mid-animation toggle does not snap.
		element.style.overflow = 'hidden';
		element.style.height = `${element.getBoundingClientRect().height}px`;
		// Commits the start value, so the target transitions within the same frame.
		void element.offsetHeight;
		element.style.height = open ? `${element.scrollHeight}px` : '0px';

		const timer = setTimeout(() => settle(element, open), DURATION_MS);
		return () => clearTimeout(timer);
	}, [open]);

	return (
		<div
			ref={ref}
			style={initialOpen ? undefined : CLOSED_STYLE}
			className={cn('transition-[height] duration-200 ease-out', className)}
			// Clipping does not remove the fields from the tab order or the accessibility tree.
			inert={!open}
		>
			<div
				className="transition-[opacity,transform] duration-200 ease-out"
				style={{ opacity: open ? 1 : 0, transform: open ? 'none' : 'translateY(-4px)' }}
			>
				{children}
			</div>
		</div>
	);
}

function settle(element: HTMLDivElement, open: boolean) {
	element.style.height = open ? 'auto' : '0px';
	element.style.overflow = open ? 'visible' : 'hidden';
}
